import { useMemo } from 'react';
import { BracketPackagePrice } from './BracketPriceTable';
import { useBracketPriceStore } from './store';
import { PackageCalculation } from '@retail/my-assortment/types';

export const useBracketPackagePrices = (
  supplierItemPackages: PackageCalculation[],
  includeVat: boolean
) => {
  const {
    newMarkups,
    setNewSalesPrice,
    setNewDiscount,
    setNewSalesCoverage,
    editingFields,
    setEditingField,
    getSalesPriceEditState,
  } = useBracketPriceStore((state) => state);

  return useMemo(() => {
    const prices: BracketPackagePrice[] = [];

    const fPak = supplierItemPackages.find((pkg) => pkg.packageType === 'PRIMARY');
    const dPak = supplierItemPackages.find((pkg) => pkg.packageType === 'SECONDARY');
    const tPak = supplierItemPackages.find((pkg) => pkg.packageType === 'TERTIARY');

    const fPakPrices = fPak!.prices[0];
    const dPakPrices = dPak?.prices[0];
    const tPakPrices = tPak?.prices[0];

    const packageGtins = { fPakGtin: fPak!.gtin, dPakGtin: dPak?.gtin, tPakGtin: tPak?.gtin };

    const editState = getSalesPriceEditState(fPakPrices);

    const primaryPrices = includeVat ? fPakPrices?.priceIncVat : fPakPrices?.priceExVat;

    if (fPak) {
      const fPakPriceInFpak = includeVat ? primaryPrices?.salesPrice : primaryPrices?.salesPrice;
      const newFPakMarkup = newMarkups?.['F-PAK'];
      prices.push({
        packageClassCode: 'F-PAK',
        id: 'fpak',
        salesPrice: {
          currentValue: fPakPriceInFpak,
          onChange: (newSalesPrice) =>
            setNewSalesPrice(newSalesPrice, packageGtins, fPakPrices, 'F-PAK', includeVat),
          newValue:
            newFPakMarkup?.salesPrice && newFPakMarkup.salesPriceIncVat
              ? {
                  value: includeVat ? newFPakMarkup.salesPriceIncVat : newFPakMarkup.salesPrice,
                  isApproximate: newMarkups?.['F-PAK']?.editedField !== 'price',
                }
              : undefined,
          editState,
        },
        salesPriceCoverage: {
          currentValue: fPakPrices?.actualCoverageDegree,
          newValue: newFPakMarkup?.salesCoverage
            ? {
                value: newFPakMarkup.salesCoverage,
                isApproximate: newMarkups?.['F-PAK']?.editedField !== 'coverage',
              }
            : undefined,
          onChange: (newSalesCoverage) =>
            setNewSalesCoverage(newSalesCoverage, packageGtins, fPakPrices, 'F-PAK'),
        },
        setEditingField: (field) => setEditingField(field, 'F-PAK'),
        editingField: editingFields?.['F-PAK'],
        unit: fPak.unit,
        consistsOfCount: fPak.consistsOfCount,
      });
    }

    if (dPak) {
      const dPakPriceInFpak = includeVat
        ? dPakPrices?.priceIncVat.salesPricePrimaryUnit
        : dPakPrices?.priceExVat.salesPricePrimaryUnit;
      const fPakPriceInFpak = primaryPrices?.salesPrice;

      const currentDiscount =
        fPakPriceInFpak && dPakPriceInFpak
          ? ((fPakPriceInFpak - dPakPriceInFpak) / fPakPriceInFpak) * 100
          : 0;
      const newDPakMarkup = newMarkups?.['D-PAK'];

      prices.push({
        packageClassCode: 'D-PAK',
        id: 'dpak',
        salesPrice: {
          currentValue: dPakPriceInFpak,
          newValue:
            newDPakMarkup?.salesPrice && newDPakMarkup.salesPriceIncVat
              ? {
                  value: includeVat ? newDPakMarkup.salesPriceIncVat : newDPakMarkup.salesPrice,
                  isApproximate: newMarkups?.['D-PAK']?.editedField !== 'price',
                }
              : undefined,
          onChange: (newSalesPrice) =>
            setNewSalesPrice(newSalesPrice, packageGtins, fPakPrices, 'D-PAK', includeVat),
          editState,
        },
        discount: {
          currentValue: currentDiscount,
          newValue: newDPakMarkup?.discount
            ? {
                value: newDPakMarkup.discount,
                isApproximate: newMarkups?.['D-PAK']?.editedField !== 'discount',
              }
            : undefined,
          onChange: (newDiscount) => setNewDiscount(newDiscount, packageGtins, fPakPrices, 'D-PAK'),
          editState,
        },
        salesPriceCoverage: {
          currentValue: dPakPrices?.actualCoverageDegree,
          newValue: newDPakMarkup?.salesCoverage
            ? {
                value: newDPakMarkup.salesCoverage,
                isApproximate: newMarkups?.['D-PAK']?.editedField !== 'coverage',
              }
            : undefined,
          onChange: (newSalesCoverage) =>
            setNewSalesCoverage(newSalesCoverage, packageGtins, fPakPrices, 'D-PAK'),
        },
        setEditingField: (field) => setEditingField(field, 'D-PAK'),
        editingField: editingFields?.['D-PAK'],
        unit: dPak.unit,
        consistsOfCount: dPak.consistsOfCount,
      });
    }

    if (tPak) {
      const tPakPriceInFpak = includeVat
        ? tPakPrices?.priceIncVat.salesPricePrimaryUnit
        : tPakPrices?.priceExVat.salesPricePrimaryUnit;
      const fPakPriceInFpak = primaryPrices?.salesPrice;

      const currentDiscount =
        fPakPriceInFpak && tPakPriceInFpak
          ? ((fPakPriceInFpak - tPakPriceInFpak) / fPakPriceInFpak) * 100
          : 0;
      const newTPakMarkup = newMarkups?.['T-PAK'];

      prices.push({
        packageClassCode: 'T-PAK',
        id: 'tpak',
        salesPrice: {
          currentValue: tPakPriceInFpak,
          newValue:
            newTPakMarkup?.salesPrice && newTPakMarkup.salesPriceIncVat
              ? {
                  value: includeVat ? newTPakMarkup.salesPriceIncVat : newTPakMarkup.salesPrice,
                  isApproximate: newMarkups?.['T-PAK']?.editedField !== 'price',
                }
              : undefined,
          onChange: (newSalesPrice) =>
            setNewSalesPrice(newSalesPrice, packageGtins, fPakPrices, 'T-PAK', includeVat),
          editState,
        },
        discount: {
          currentValue: currentDiscount,
          newValue: newTPakMarkup?.discount
            ? {
                value: newTPakMarkup.discount,
                isApproximate: newMarkups?.['T-PAK']?.editedField !== 'discount',
              }
            : undefined,
          onChange: (newDiscount) => setNewDiscount(newDiscount, packageGtins, fPakPrices, 'T-PAK'),
          editState,
        },
        salesPriceCoverage: {
          currentValue: tPakPrices?.actualCoverageDegree,
          newValue: newTPakMarkup?.salesCoverage
            ? {
                value: newTPakMarkup.salesCoverage,
                isApproximate: newMarkups?.['T-PAK']?.editedField !== 'coverage',
              }
            : undefined,
          onChange: (newSalesCoverage) =>
            setNewSalesCoverage(newSalesCoverage, packageGtins, fPakPrices, 'T-PAK'),
        },
        setEditingField: (field) => setEditingField(field, 'T-PAK'),
        editingField: editingFields?.['T-PAK'],
        unit: tPak.unit,

        consistsOfCount: tPak.consistsOfCount,
      });
    }

    return prices;
  }, [
    editingFields,
    getSalesPriceEditState,
    includeVat,
    newMarkups,
    setEditingField,
    setNewDiscount,
    setNewSalesCoverage,
    setNewSalesPrice,
    supplierItemPackages,
  ]);
};
